<script lang="ts">
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { resizeIssuePhoto } from '$lib/utils/photos';
  import { relativeTime, formatIST } from '$lib/utils/dates';
  import TcsNumber from '../ui/TcsNumber.svelte';
  import { onDestroy, untrack } from 'svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { X, ChevronDown, Check, MapPin, Paperclip, Send, Pencil, Rocket } from 'lucide-svelte';
  import { issuesApi, issueCommentsApi, issueAttachmentsApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { apiFetch, apiFetchBlob, getErrorMessage } from '$lib/api/client';
  import type { AssignableUser, Issue, IssueStatus } from '$lib/types';
  import CustomerCallForm from './CustomerCallForm.svelte';
  import RecordRead from '../RecordRead.svelte';
  import { requiresResolution, resolutionError, resolutionHint } from '$lib/utils/issueResolution';

  const {
    issueId,
    assignableUsers,
    autoResolve = false,
    onArmConsumed = () => {},
    onClose,
    onEdit
  }: {
    issueId: number;
    assignableUsers: AssignableUser[];
    // Set by IssuesPage's board when a card is dragged into the Resolved
    // column: opens the panel straight into the resolution form below instead of
    // a bare row click, so the resolve gate (see updateStatusMutation) is
    // never bypassed by a drop. Same effect as clicking the status pill and
    // picking "Resolved" by hand — see the effect below.
    autoResolve?: boolean;
    // Called once, the moment the auto-arm actually opens the resolution form, so
    // the parent can retire the arm (set boardResolveArm = null). That makes
    // autoResolve go false, which is what keeps the arm strictly one-shot —
    // see the effect below.
    onArmConsumed?: () => void;
    onClose: () => void;
    // Opens IssuesPage's edit modal prefilled with the issue this panel has
    // already loaded (issueQuery.data) — passed back up rather than just an
    // id, since IssuesPage may not have this row cached (e.g. a direct
    // #/issues/<id> link). The edit control is hidden for issues-only users.
    onEdit: (issue: Issue) => void;
  } = $props();

  const queryClient = useQueryClient();
  let customerCallOpen = $state(false);
  const isIssuesOnly = $derived(session.me?.user.issues_only ?? false);

  const STATUS_META: Record<IssueStatus, { label: string; bg: string; fg: string }> = {
    open: { label: 'Open', bg: '#ECF8CC', fg: '#5c7a00' },
    in_progress: { label: 'In progress', bg: '#FBF0DC', fg: '#8a5c00' },
    resolved: { label: 'Resolved', bg: '#E6E9EE', fg: '#10223A' }
  };
  const STATUS_ORDER: IssueStatus[] = ['open', 'in_progress', 'resolved'];

  let confirmedIssueGet = $state<{ id: number; userId?: number; assignmentVersion?: number } | null>(null);
  const issueQuery = createQuery(() => ({
    queryKey: ['issue-detail', issueId],
    queryFn: async () => {
      const requestedId = issueId, userId = session.me?.user.id;
      const row = await issuesApi.get(requestedId);
      if (row.id === requestedId && issueId === requestedId && session.me?.user.id === userId) confirmedIssueGet = { id: row.id, userId, assignmentVersion: row.assignment_version };
      return row;
    }
  }));
  const commentsQuery = createQuery(() => ({
    queryKey: ['issue-comments', issueId],
    queryFn: () => issueCommentsApi.list(issueId)
  }));
  const teamUsersQuery = createQuery(() => ({
    queryKey: ['assignable-users', issueQuery.data?.vertical_id],
    queryFn: () => apiFetch<{ results: AssignableUser[] }>(`/users/assignable?vertical_id=${issueQuery.data?.vertical_id}`),
    enabled: !isIssuesOnly && issueQuery.data?.id === issueId
  }));
  const teamUsers = $derived(teamUsersQuery.data?.results ?? assignableUsers.filter((u) => u.vertical_ids?.includes(issueQuery.data?.vertical_id ?? 0)));
  const satisfactionOptions = [
    { value: '', label: 'Not recorded' },
    { value: 'pending', label: 'Pending confirmation' },
    { value: 'satisfied', label: 'Satisfied' },
    { value: 'not_satisfied', label: 'Not satisfied' }
  ];
  const satisfactionMutation = createMutation(() => ({
    mutationFn: (input: { id: number; generation: number; vertical_id: number; value: string }) => issuesApi.update(input.id, { customer_satisfaction: input.value, vertical_id: input.vertical_id }),
    onSuccess: async (saved, input) => { await cacheSavedIssue(input.id, saved, input.vertical_id); },
    onError: (err: unknown, input) => { invalidateIssue(input.id); if (issueId === input.id && draftGeneration === input.generation) toast.error(getErrorMessage(err)); }
  }));
  const attachmentsQuery = createQuery(() => ({
    queryKey: ['issue-attachments', issueId],
    queryFn: () => issueAttachmentsApi.list(issueId)
  }));

  function userName(id: number | null): string {
    if (!id) return '';
    if (id === session.me?.user.id) return session.me.user.name || 'You';
    return assignableUsers.find((u) => u.id === id)?.name ?? 'Team member';
  }

  let statusMenuOpen = $state(false);
  function invalidateIssue(id: number) {
    for (const key of [['issue-detail', id], ['issue-comments', id], ['issues'], ['issues-count'], ['issues-board']]) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  }

  async function cacheSavedIssue(id: number, saved: Issue, expectedTeam: number, handover = false, beforePublish = () => {}) {
    // Retire pre-save reads before publishing the full response. This also
    // updates the correct cache if the panel switched issues during the save.
    const previous = queryClient.getQueryData<Issue>(['issue-detail', id]);
    await queryClient.cancelQueries({ queryKey: ['issue-detail', id], exact: true }, { revert: false });
    const current = queryClient.getQueryData<Issue>(['issue-detail', id]);
    // A late response from the old team must not roll back a newer refetch or
    // handover. Only this panel's explicit handover may publish a changed team.
    if ([previous, current].some((row) => row && (
      Date.parse(row.updated_at) > Date.parse(saved.updated_at) ||
      (row.vertical_id !== saved.vertical_id && (!handover || row.vertical_id !== expectedTeam))
    ))) {
      invalidateIssue(id);
      return false;
    }
    beforePublish();
    queryClient.setQueryData(['issue-detail', id], saved);
    invalidateIssue(id);
    return true;
  }

  let draftIssueId = $state<number | null>(null);
  let draftGeneration = $state(0);
  let draftVerticalId = $state<number | null>(null);
  let teamConflict = $state('');
  let nextAction = $state('');
  let nextActionDate = $state('');
  let assigneeId = $state<number | null>(null);
  let followupBaseline = $state('');
  let followupMessage = $state('');
  let followupError = $state('');
  const followupSnapshot = $derived(JSON.stringify([nextAction, nextActionDate, assigneeId]));
  const followupDirty = $derived(followupSnapshot !== followupBaseline);
  const supportTeams = $derived(session.me?.issue_verticals ?? session.me?.verticals ?? []);
  const handoffTeams = $derived(supportTeams.filter((v) => v.id !== issueQuery.data?.vertical_id));
  let handoffOpen = $state(false);
  let handoffTeamId = $state<number | null>(null);
  let handoffAssigneeId = $state<number | null>(null);
  let handoffNote = $state('');
  let handoffError = $state('');
  const handoffUsersQuery = createQuery(() => ({
    queryKey: ['assignable-users', handoffTeamId],
    queryFn: () => apiFetch<{ results: AssignableUser[] }>(`/users/assignable?vertical_id=${handoffTeamId}`),
    enabled: handoffOpen && !isIssuesOnly && handoffTeamId !== null && issueQuery.data?.id === issueId
  }));
  const handoffMutation = createMutation(() => ({
    mutationFn: (input: { id: number; generation: number; expected_vertical_id: number; vertical_id: number; assignee_user_id: number | null; note: string }) =>
      apiFetch<Issue>(`/issues/${input.id}/handoff`, {
        method: 'POST', body: JSON.stringify({ vertical_id: input.vertical_id, expected_vertical_id: input.expected_vertical_id, assignee_user_id: input.assignee_user_id, note: input.note })
      }),
    onSuccess: async (saved, input) => {
      const accepted = await cacheSavedIssue(input.id, saved, input.expected_vertical_id, true, () => {
        if (issueId === input.id && draftGeneration === input.generation) adoptTeamDrafts(saved);
      });
      if (!accepted) return;
      if (issueId !== input.id || draftGeneration !== input.generation) return;
      handoffOpen = false;
      handoffNote = '';
      handoffError = '';
      toast.success('Handed over. Reporter details and case history retained.');
    },
    onError: (err: unknown, input) => {
      invalidateIssue(input.id);
      if (issueId === input.id && draftGeneration === input.generation) handoffError = getErrorMessage(err);
    }
  }));

  function openHandoff() {
    if (isIssuesOnly || followupDirty || handoffBusy || !draftTeamMatches()) return;
    handoffTeamId = handoffTeams[0]?.id ?? null;
    handoffAssigneeId = null;
    handoffNote = '';
    handoffError = '';
    handoffOpen = true;
  }
  function confirmHandoff() {
    if (isIssuesOnly || followupDirty || handoffBusy || !draftTeamMatches() || draftVerticalId === null || !handoffTeamId || !handoffTeams.some((v) => v.id === handoffTeamId)) return;
    handoffError = '';
    handoffMutation.mutate({ id: issueId, generation: draftGeneration, expected_vertical_id: draftVerticalId, vertical_id: handoffTeamId, assignee_user_id: handoffAssigneeId, note: handoffNote.trim() });
  }
  function draftTeamMatches() {
    return !teamConflict && issueQuery.data?.id === issueId && draftVerticalId !== null && issueQuery.data.vertical_id === draftVerticalId;
  }
  function adoptTeamDrafts(issue: Issue) {
    draftVerticalId = issue.vertical_id;
    nextAction = issue.next_action ?? '';
    nextActionDate = issue.next_action_date?.slice(0, 10) ?? '';
    assigneeId = issue.assignee_user_id;
    followupBaseline = JSON.stringify([nextAction, nextActionDate, assigneeId]);
    followupError = '';
    followupMessage = '';
    handoffOpen = false;
    handoffError = '';
    handoffNote = '';
    statusMenuOpen = false;
    resolvePanelOpen = false;
    resolveError = null;
    capaRootCause = '';
    resolvingTechnicianName = '';
    capaCorrectiveAction = '';
    capaPreventiveAction = '';
    teamConflict = '';
  }
  function resetTeamDrafts() {
    if (issueQuery.data?.id !== issueId || handoffBusy) return;
    draftGeneration++;
    adoptTeamDrafts(issueQuery.data);
  }
  function saveSatisfaction(value: string) {
    if (isIssuesOnly || !draftTeamMatches() || draftVerticalId === null || handoffBusy) return;
    satisfactionMutation.mutate({ id: issueId, generation: draftGeneration, vertical_id: draftVerticalId, value });
  }
  function discardFollowup() {
    const issue = issueQuery.data;
    if (issue?.id !== issueId || handoffBusy || teamConflict) return;
    nextAction = issue.next_action ?? '';
    nextActionDate = issue.next_action_date?.slice(0, 10) ?? '';
    assigneeId = issue.assignee_user_id;
    followupBaseline = JSON.stringify([nextAction, nextActionDate, assigneeId]);
    followupError = '';
    followupMessage = '';
  }

  $effect(() => {
    const id = issueId;
    const issue = issueQuery.data;
    untrack(() => {
      if (draftIssueId !== id) {
        customerCallOpen = false;
        draftIssueId = id;
        draftGeneration++;
        draftVerticalId = null;
        teamConflict = '';
        nextAction = '';
        nextActionDate = '';
        assigneeId = null;
        followupBaseline = JSON.stringify(['', '', null]);
        followupMessage = '';
        followupError = '';
        handoffOpen = false;
        handoffTeamId = null;
        handoffAssigneeId = null;
        handoffNote = '';
        handoffError = '';
        commentText = '';
        statusMenuOpen = false;
        resolvePanelOpen = false;
        resolveError = null;
      }
      if (issue?.id === id && draftVerticalId !== null && issue.vertical_id !== draftVerticalId) {
        if (followupDirty || handoffOpen || resolvePanelOpen || statusMenuOpen) {
          teamConflict = 'This case changed support teams while you were editing. Discard old team drafts and reload the current team before making changes.';
          return;
        }
        adoptTeamDrafts(issue);
      }
      if (issue?.id === id && !followupDirty && !handoffOpen && !resolvePanelOpen && !statusMenuOpen && !teamConflict) {
        draftVerticalId = issue.vertical_id;
        nextAction = issue.next_action ?? '';
        nextActionDate = issue.next_action_date?.slice(0, 10) ?? '';
        assigneeId = issue.assignee_user_id;
        followupBaseline = JSON.stringify([nextAction, nextActionDate, assigneeId]);
      }
    });
  });

  const followupMutation = createMutation(() => ({
    mutationFn: (input: { id: number; generation: number; vertical_id: number; clear: boolean; body: { next_action: string; next_action_date: string | null; assignee_user_id?: number | null } }) =>
      issuesApi.update(input.id, { ...input.body, vertical_id: input.vertical_id }),
    onSuccess: async (saved, input) => {
      if (!await cacheSavedIssue(input.id, saved, input.vertical_id)) return;
      if (issueId !== input.id || draftGeneration !== input.generation) return;
      nextAction = saved.next_action ?? '';
      nextActionDate = saved.next_action_date?.slice(0, 10) ?? '';
      assigneeId = saved.assignee_user_id;
      followupBaseline = JSON.stringify([nextAction, nextActionDate, assigneeId]);
      followupMessage = input.clear ? 'Follow-up cleared. This record is still open.' : 'Follow-up saved.';
    },
    onError: (err: unknown, input) => {
      invalidateIssue(input.id);
      if (issueId === input.id && draftGeneration === input.generation) followupError = getErrorMessage(err);
    }
  }));

  function saveFollowup(clear = false) {
    if (isIssuesOnly || !draftTeamMatches() || draftVerticalId === null || issueQuery.data?.status === 'resolved' || followupMutation.isPending || satisfactionMutation.isPending || handoffMutation.isPending) return;
    followupMessage = '';
    followupError = '';
    followupMutation.mutate({
      id: issueId, generation: draftGeneration, vertical_id: draftVerticalId, clear,
      // Clearing a reminder never changes assignment or closes the record.
      body: clear ? { next_action: '', next_action_date: null } : {
        next_action: nextAction.trim(), next_action_date: nextActionDate || null, assignee_user_id: assigneeId
      }
    });
  }

  function dueLabel(date: string): string {
    return new Date(`${date.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
  }
  function dueBadge(date: string): string {
    const day = date.slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return day < today ? 'Overdue' : day === today ? 'Today' : '';
  }
  const updateStatusMutation = createMutation(() => ({
    mutationFn: ({ id, generation, ...body }: { id: number; generation: number; vertical_id: number; status: IssueStatus; resolving_technician_name?: string; capa_root_cause?: string; capa_corrective_action?: string; capa_preventive_action?: string }) =>
      issuesApi.update(id, body),
    onSuccess: async (saved, input) => {
      if (!await cacheSavedIssue(input.id, saved, input.vertical_id)) return;
      if (issueId !== input.id || draftGeneration !== input.generation) return;
      statusMenuOpen = false;
      resolvePanelOpen = false;
      resolveError = null;
    },
    onError: (err: unknown, input) => {
      invalidateIssue(input.id);
      if (issueId === input.id && draftGeneration === input.generation) resolveError = getErrorMessage(err);
    }
  }));

  // Resolving requires a technician name, plus CAPA for complaints only.
  // Picking "Resolved" opens
  // this inline form instead of firing the mutation immediately; other
  // statuses still update on click.
  let resolvePanelOpen = $state(false);
  let capaRootCause = $state('');
  let resolvingTechnicianName = $state('');
  let capaCorrectiveAction = $state('');
  let capaPreventiveAction = $state('');
  let resolveError = $state<string | null>(null);

  function pickStatus(current: IssueStatus, s: IssueStatus) {
    if (isIssuesOnly || updateStatusMutation.isPending || followupMutation.isPending || satisfactionMutation.isPending || handoffMutation.isPending || !draftTeamMatches() || draftVerticalId === null) return;
    statusMenuOpen = false;
    if (requiresResolution(current, s)) {
      resolvingTechnicianName = issueQuery.data?.resolving_technician_name || session.me?.user.name || '';
      capaRootCause = issueQuery.data?.capa_root_cause ?? '';
      capaCorrectiveAction = issueQuery.data?.capa_corrective_action ?? '';
      capaPreventiveAction = issueQuery.data?.capa_preventive_action ?? '';
      resolveError = null;
      resolvePanelOpen = true;
      return;
    }
    updateStatusMutation.mutate({ id: issueId, generation: draftGeneration, vertical_id: draftVerticalId, status: s });
  }

  // See the autoResolve prop doc above. A board drop into Resolved means
  // "open the resolution form once for this issue". The arm is strictly ONE-SHOT:
  // the instant this issue's own row has loaded we consume the arm
  // (onArmConsumed flips the parent's boardResolveArm to null, so autoResolve
  // goes false) and open the form exactly as a manual "Resolved" pick does.
  //
  // Because the very first guard bails whenever autoResolve is false, nothing
  // can re-open the form once the arm is consumed: Cancel, a successful
  // resolve (whose query invalidation refetch is async, so issueQuery.data is
  // briefly stale at 'open'), Escape, or an error all leave autoResolve false,
  // so no reactive read here — not resolvePanelOpen, not stale issue status —
  // can route back into this branch. Only a brand-new drop re-arms it, by
  // setting boardResolveArm === issueId again. No dismissal flags, no
  // dependence on refetch timing.
  $effect(() => {
    if (!autoResolve) return;
    const issue = issueQuery.data;
    // issue.id !== issueId: on a card→card switch the reused instance can
    // still hold the previous issue's cached row for a tick — only fire once
    // THIS issue's data has loaded, so status and CAPA prefill are correct.
    if (!issue || issue.id !== issueId || issue.status === 'resolved') return;
    onArmConsumed();
    pickStatus(issue.status, 'resolved');
  });

  // "Send to dev": hands the issue to Genba as a ticket. Hidden entirely
  // when the backend isn't configured (session.me.genba_enabled, set from
  // config.GenbaEnabled via GET /api/me) rather than shown-then-erroring —
  // a 503 only surfaces if that flag is somehow stale.
  const sendToDevMutation = createMutation(() => ({
    mutationFn: (input: { id: number; generation: number; vertical_id: number }) => apiFetch<Issue>(`/issues/${input.id}/send-to-dev`, { method: 'POST', body: JSON.stringify({ vertical_id: input.vertical_id }) }),
    onSuccess: async (saved, input) => {
      if (!await cacheSavedIssue(input.id, saved, input.vertical_id)) return;
      if (issueId === input.id && draftGeneration === input.generation) toast.success('Sent to dev.');
    },
    onError: (err: unknown, input) => { invalidateIssue(input.id); if (issueId === input.id && draftGeneration === input.generation) toast.error(getErrorMessage(err)); }
  }));
  async function sendToDev() {
    if (handoffMutation.isPending || !draftTeamMatches() || draftVerticalId === null) return;
    if (!await confirmAction('Send this issue to dev as a Genba ticket? This can only be done once.')) return;
    sendToDevMutation.mutate({ id: issueId, generation: draftGeneration, vertical_id: draftVerticalId });
  }

  const handoffBusy = $derived(handoffMutation.isPending || followupMutation.isPending || updateStatusMutation.isPending || satisfactionMutation.isPending || sendToDevMutation.isPending);
  const editDisabled = $derived(!!teamConflict || followupDirty || handoffBusy || issueQuery.isFetching);
  const editTitle = $derived(teamConflict ? 'Discard old team drafts before editing.' : handoffBusy
    ? 'Wait for the current update to finish before editing.'
    : followupDirty ? 'Save or clear your follow-up changes before editing the full record.'
    : issueQuery.isFetching ? 'Refreshing this record. Please wait before editing.' : 'Edit issue');

  function editIssue() {
    if (editDisabled || isIssuesOnly) return;
    const issue = queryClient.getQueryData<Issue>(['issue-detail', issueId]);
    if (issue?.id === issueId) onEdit(issue);
  }

  function confirmResolve() {
    if (isIssuesOnly || !draftTeamMatches() || draftVerticalId === null || updateStatusMutation.isPending || followupMutation.isPending || satisfactionMutation.isPending || handoffMutation.isPending) return;
    const root = capaRootCause.trim();
    const corrective = capaCorrectiveAction.trim();
    const preventive = capaPreventiveAction.trim();
    const category = issueQuery.data?.category ?? 'issue';
    resolveError = resolutionError(issueQuery.data?.status, { status: 'resolved', category, resolving_technician_name: resolvingTechnicianName, capa_root_cause: root, capa_corrective_action: corrective, capa_preventive_action: preventive });
    if (resolveError) return;
    updateStatusMutation.mutate({
      id: issueId,
      generation: draftGeneration,
      vertical_id: draftVerticalId,
      status: 'resolved',
      resolving_technician_name: resolvingTechnicianName.trim(),
      ...(category === 'issue' ? { capa_root_cause: root, capa_corrective_action: corrective, capa_preventive_action: preventive } : {})
    });
  }

  // Photos are served through auth (GET /api/attachments/:id/file), so a
  // plain <img src> can't load them — fetch each as a blob once and cache
  // the object URL, revoking on unmount.
  let blobUrls = $state<Record<number, string>>({});
  $effect(() => {
    const rows = attachmentsQuery.data?.results ?? [];
    for (const a of rows) {
      if (blobUrls[a.id]) continue;
      apiFetchBlob(`/attachments/${a.id}/file`).then((url) => {
        blobUrls = { ...blobUrls, [a.id]: url };
      });
    }
  });
  onDestroy(() => {
    for (const url of Object.values(blobUrls)) URL.revokeObjectURL(url);
  });

  let lightboxUrl = $state<string | null>(null);

  let commentText = $state('');
  let commentInput: HTMLTextAreaElement | undefined = $state();
  function prefillOutcomeNote(outcome: 'Fixed' | 'Need parts') {
    const prefix = `${outcome}: `;
    if (!commentText.trim()) commentText = prefix;
    else if (!commentText.includes(prefix)) commentText = `${commentText.trimEnd()}\n\n${prefix}`;
    commentInput?.focus();
  }
  let restoredDraftKey = $state('');
  let draftMessage = $state('');
  const commentDraftKey = $derived(`toyama:issue-note:${session.me?.user.id ?? 'anonymous'}:${issueId}`);
  $effect(() => {
    const key = commentDraftKey;
    untrack(() => {
      try { commentText = localStorage.getItem(key) || ''; draftMessage = commentText ? 'Draft restored from this device' : ''; }
      catch { commentText = ''; draftMessage = 'Device draft storage unavailable'; }
      restoredDraftKey = key;
    });
  });
  $effect(() => {
    const key = commentDraftKey, body = commentText;
    if (restoredDraftKey !== key) return;
    try { if (body) localStorage.setItem(key, body); else localStorage.removeItem(key); }
    catch { draftMessage = 'Unable to save draft on this device'; }
  });
  const sendCommentMutation = createMutation(() => ({
    mutationFn: (input: { id: number; body: string; generation: number }) => issueCommentsApi.create(input.id, input.body),
    onSuccess: (_, input) => {
      const key = `toyama:issue-note:${session.me?.user.id ?? 'anonymous'}:${input.id}`;
      try { if (localStorage.getItem(key)?.trim() === input.body) localStorage.removeItem(key); } catch { /* Posting succeeds without device storage. */ }
      if (issueId === input.id && draftGeneration === input.generation && commentText.trim() === input.body) { commentText = ''; draftMessage = 'Interaction saved'; }
      queryClient.invalidateQueries({ queryKey: ['issue-comments', input.id] });
    }
  }));
  function sendComment() {
    const body = commentText.trim();
    if (!body) return;
    sendCommentMutation.mutate({ id: issueId, body, generation: draftGeneration });
  }

  let fileInput: HTMLInputElement | undefined = $state();
  type PhotoUpload = { key: number; issueId: number; file: File; status: 'waiting' | 'uploading' | 'done' | 'failed'; error?: string };
  let photoUploads = $state<PhotoUpload[]>([]);
  let photoSequence = 0;
  async function uploadPhoto(item: PhotoUpload) {
    photoUploads = photoUploads.map(p => p.key === item.key ? { ...p, status: 'uploading', error: undefined } : p);
    try {
      const file = await resizeIssuePhoto(item.file);
      await issueAttachmentsApi.upload(item.issueId, file);
      queryClient.invalidateQueries({ queryKey: ['issue-attachments', item.issueId] });
      photoUploads = photoUploads.map(p => p.key === item.key ? { ...p, status: 'done' } : p);
    } catch (err) {
      photoUploads = photoUploads.map(p => p.key === item.key ? { ...p, status: 'failed', error: getErrorMessage(err) } : p);
      toast.error(`Photo “${item.file.name}”: ${getErrorMessage(err)}`);
    }
  }
  const uploadMutation = createMutation(() => ({
    mutationFn: (input: { id: number; file: File }) => issueAttachmentsApi.upload(input.id, input.file),
    onSuccess: (_, input) => queryClient.invalidateQueries({ queryKey: ['issue-attachments', input.id] })
  }));
  async function onFileChosen(e: Event) {
    const files = [...((e.currentTarget as HTMLInputElement).files ?? [])];
    const selected = files.map(file => ({ key: ++photoSequence, issueId, file, status: 'waiting' as const }));
    photoUploads = [...photoUploads.filter(p => p.status !== 'done'), ...selected];
    (e.currentTarget as HTMLInputElement).value = '';
    for (const item of selected) await uploadPhoto(item);
  }

  function timeAgo(iso: string): string {
    return relativeTime(iso);
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && !lightboxUrl) onClose();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="issue-panel-backdrop" role="presentation" onclick={onClose}></div>

<div class="issue-panel" data-screen-label="Issue detail slide-over">
  {#if issueQuery.data?.id === issueId}
    {@const issue = issueQuery.data}
    <RecordRead kind="issues" recordId={issueId} dataId={issue.id} assignmentVersion={issue.assignment_version} ready={issueQuery.isSuccess && !issueQuery.isFetching && confirmedIssueGet?.id === issueId && confirmedIssueGet.userId === session.me?.user.id && confirmedIssueGet.assignmentVersion === issue.assignment_version} />
    {@const meta = STATUS_META[issue.status]}
    <div class="flex shrink-0 items-center gap-2.5 border-b border-[#EAE9E8] px-[18px] py-[13px]">
      <div class="relative">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-xs font-bold"
          style="background:{meta.bg}; color:{meta.fg}"
          disabled={!!teamConflict || isIssuesOnly || updateStatusMutation.isPending || followupMutation.isPending || satisfactionMutation.isPending || handoffMutation.isPending}
          onclick={() => (statusMenuOpen = !statusMenuOpen)}
        >
          <span class="h-[7px] w-[7px] rounded-full" style="background:{meta.fg}"></span>
          {meta.label}
          <ChevronDown class="h-3.5 w-3.5" />
        </button>
        {#if statusMenuOpen}
          <button
            type="button"
            class="fixed inset-0 z-10 cursor-default"
            aria-label="Close status menu"
            onclick={() => (statusMenuOpen = false)}
          ></button>
          <div
            class="absolute left-0 top-full z-20 mt-1.5 w-[180px] rounded-[11px] border border-toyama-cloudy-gray bg-white p-1.5 shadow-[var(--tym-shadow-3)]"
          >
            {#each STATUS_ORDER as s (s)}
              {@const m = STATUS_META[s]}
              <button
                type="button"
                class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
                onclick={() => pickStatus(issue.status, s)}
              >
                <span class="h-2 w-2 rounded-full" style="background:{m.fg}"></span>
                <span class="flex-1 text-[13px] font-semibold text-navy">{m.label}</span>
                {#if s === issue.status}<Check class="h-3.5 w-3.5 text-toyama-teal" />{/if}
              </button>
            {/each}
          </div>
        {/if}
      </div>
      <span class="font-heading text-xs font-bold tracking-[.04em] text-dark-gray">#{issue.id}</span>
      {#if issue.order_number}<TcsNumber value={issue.order_number} />{/if}
      <span class="flex-1"></span>
      {#if issue.source === 'wizhom'}
        <span class="inline-flex items-center gap-1 rounded-md bg-[#E6E9EE] px-2.5 py-1 text-[11px] font-bold text-navy"
          >App ticket</span
        >
      {/if}
      {#if issue.genba_ticket_id}
        <span
          class="inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[11px] font-bold"
          style="background:#E3F0F1; color:#226A73"
        >
          <Rocket class="h-3 w-3" />
          Sent to dev
        </span>
      {:else if session.me?.genba_enabled}
        <button
          type="button"
          class="flex h-[34px] items-center gap-1.5 rounded-[9px] border border-[#EAE9E8] px-3 text-xs font-bold text-[#666] hover:bg-toyama-light-carbon disabled:opacity-50"
          onclick={sendToDev}
          disabled={!!teamConflict || sendToDevMutation.isPending || handoffMutation.isPending}
        >
          <Rocket class="h-3.5 w-3.5" />
          Send to dev
        </button>
      {/if}
      {#if !isIssuesOnly}<button
        type="button"
        title={editTitle}
        disabled={editDisabled}
        class="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[#EAE9E8] text-[#666] hover:bg-toyama-light-carbon disabled:cursor-not-allowed disabled:opacity-50"
        onclick={editIssue}
      >
        <Pencil class="h-4 w-4" />
      </button>{/if}
      <button
        type="button"
        title="Close (Esc)"
        class="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[#EAE9E8] text-[#666] hover:bg-toyama-light-carbon"
        onclick={onClose}
      >
        <X class="h-4 w-4" />
      </button>
    </div>

    {#if resolveError && !resolvePanelOpen}
      <p class="px-[18px] py-2 text-xs text-red-600">{resolveError}</p>
    {/if}
    {#if resolvePanelOpen}
      <div class="shrink-0 border-b border-[#EAE9E8] bg-[#FBFBFA] px-[18px] py-4">
        <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">
          Resolution details{issue.category === 'issue' ? ' · CAPA required' : ''}
        </div>
        <p class="mb-2.5 text-xs text-dark-gray">{resolutionHint(supportTeams.find(team => team.id === issue.vertical_id)?.name)}</p>
        <div class="mb-2.5">
          <label class="label" for="resolve-technician-name">Resolving technician name</label>
          <input id="resolve-technician-name" class="input" bind:value={resolvingTechnicianName} required />
          <p class="mt-1 text-xs text-dark-gray">Confirm the actual technician's name. Your name is only a starting suggestion; submitting still requires the resolution details below.</p>
        </div>
        {#if issue.category === 'issue'}
        <div class="mb-2.5">
          <label class="label" for="capa-root-cause">Root cause</label>
          <textarea id="capa-root-cause" bind:value={capaRootCause} rows="2" class="input resize-none"></textarea>
        </div>
        <div class="mb-2.5">
          <label class="label" for="capa-corrective-action">Corrective action</label>
          <textarea id="capa-corrective-action" bind:value={capaCorrectiveAction} rows="2" class="input resize-none"
          ></textarea>
        </div>
        <div class="mb-2.5">
          <label class="label" for="capa-preventive-action">Preventive action</label>
          <textarea id="capa-preventive-action" bind:value={capaPreventiveAction} rows="2" class="input resize-none"
          ></textarea>
        </div>
        {/if}
        {#if resolveError}
          <div class="mb-2.5 text-xs font-medium text-red-600">{resolveError}</div>
        {/if}
        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="btn-secondary !h-9 !px-3 text-xs"
            onclick={() => {
              resolvePanelOpen = false;
              resolveError = null;
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            class="flex h-9 items-center gap-1.5 rounded-[11px] bg-toyama-lime px-3.5 font-heading text-xs font-bold text-navy hover:bg-[var(--tym-lime-600)] disabled:opacity-50"
            onclick={confirmResolve}
            disabled={!!teamConflict || updateStatusMutation.isPending || handoffMutation.isPending}
          >
            <Check class="h-3.5 w-3.5" />
            Mark resolved
          </button>
        </div>
      </div>
    {/if}

    <div class="min-h-0 flex-1 overflow-y-auto px-[22px] py-5">
      <h3 class="mb-3.5 font-heading text-xl font-bold leading-[1.3] tracking-tight text-navy">{issue.title}</h3>
      {#if issue.status === 'resolved'}
        <p class="mb-3 text-sm"><strong>Resolving technician:</strong> {issue.resolving_technician_name?.trim() || 'Not recorded'}</p>
      {/if}
      {#if isIssuesOnly}<p class="mb-3 text-xs text-dark-gray">{resolutionHint(supportTeams.find(team => team.id === issue.vertical_id)?.name)}</p>{/if}
      {#if teamConflict}
        <div class="mb-4 space-y-2 rounded-xl border border-toyama-cloudy-gray p-4" role="alert">
          <p class="text-sm text-navy">{teamConflict}</p>
          <button type="button" class="btn-secondary" disabled={handoffBusy || issueQuery.isFetching} onclick={resetTeamDrafts}>Discard old team drafts &amp; use current team</button>
        </div>
      {/if}

      {#if !isIssuesOnly && handoffTeams.length > 0}
        <section class="mb-4 space-y-3 rounded-xl border border-[#EFEEED] bg-[#FBFBFA] p-4" aria-label="Team handover">
          <p class="text-xs text-dark-gray">Support team: {supportTeams.find((v) => v.id === issue.vertical_id)?.name ?? 'Current team'}</p>
          {#if followupDirty}
            <p class="text-xs text-toyama-teal">Save or discard your unsaved follow-up changes before handing over.</p>
            <button type="button" class="btn-secondary" disabled={handoffBusy} onclick={discardFollowup}>Discard follow-up changes</button>
          {/if}
          {#if !handoffOpen}
            <button type="button" class="btn-secondary" disabled={!!teamConflict || followupDirty || handoffBusy || issueQuery.isFetching || resolvePanelOpen} onclick={openHandoff}>Hand over to another team</button>
          {:else}
            <p class="text-xs text-dark-gray">Company and linked contact references outside the destination team will be unlinked. Reporter details, location, follow-up, satisfaction, CAPA, comments and photos stay with this case. Your destination team access keeps this case visible to you.</p>
            <fieldset class="space-y-3" disabled={!!teamConflict || handoffBusy}>
              <div><label class="label" for="handoff-team">Destination support team</label>
                <select id="handoff-team" class="input" bind:value={handoffTeamId} onchange={() => { handoffAssigneeId = null; handoffError = ''; }}>
                  {#each handoffTeams as team (team.id)}<option value={team.id}>{team.name}</option>{/each}
                </select></div>
              <div><label class="label" for="handoff-assignee">Assigned to</label>
                <select id="handoff-assignee" class="input" bind:value={handoffAssigneeId} disabled={handoffUsersQuery.isFetching || handoffUsersQuery.isError}>
                  <option value={null}>Unassigned</option>
                  {#each handoffUsersQuery.data?.results ?? [] as user (user.id)}<option value={user.id}>{user.name}</option>{/each}
                </select>
                {#if handoffUsersQuery.isError}<p class="mt-1 text-xs text-red-600">{getErrorMessage(handoffUsersQuery.error)}</p>{/if}
              </div>
              <div><label class="label" for="handoff-note">Handover note (optional)</label>
                <textarea id="handoff-note" class="input" rows="2" bind:value={handoffNote} placeholder="What does the receiving team need to know?"></textarea></div>
              <div class="flex flex-wrap gap-2">
                <button type="button" class="btn-primary" disabled={followupDirty || !handoffTeamId || handoffUsersQuery.isFetching || handoffUsersQuery.isError || resolvePanelOpen} onclick={confirmHandoff}>{handoffMutation.isPending ? 'Handing over…' : 'Confirm handover'}</button>
                <button type="button" class="btn-secondary" onclick={() => (handoffOpen = false)}>Cancel</button>
              </div>
            </fieldset>
            {#if handoffError}<p class="text-xs text-red-600" role="alert">{handoffError}</p>{/if}
          {/if}
        </section>
      {/if}

      {#if !isIssuesOnly}
        <div class="mb-4">
          <button type="button" class="btn-primary" disabled={!!teamConflict || handoffBusy || resolvePanelOpen}
            onclick={() => pickStatus(issue.status, issue.status === 'resolved' ? 'open' : 'resolved')}>
            {updateStatusMutation.isPending ? 'Updating…' : issue.status === 'resolved' ? 'Reopen' : issue.category === 'customer_feedback' ? 'Close feedback' : issue.category === 'customer_log' ? 'Close log' : 'Fixed — review resolution'}
          </button>
          <p class="mt-1 text-xs text-dark-gray">{issue.status === 'resolved' ? 'Team work is resolved. Confirm with the customer below; reopen only if more work is needed.' : issue.category === 'issue' ? 'Review the cause, fix and prevention before resolving. Customer confirmation follows separately.' : 'Close when no further work is needed. Customer confirmation follows separately; no CAPA required.'}</p>
        </div>
      {/if}

      <section class="mb-4 space-y-3 rounded-xl border border-[#EFEEED] bg-[#FBFBFA] p-4" aria-label="Follow-up">
        <h4 class="font-heading text-sm font-bold text-navy">Next action &amp; follow-up</h4>
        {#if !isIssuesOnly && issue.status !== 'resolved'}
          <p class="text-xs text-dark-gray">What happens next, when, and who is responsible? Saving a follow-up does not close this record.</p>
          <fieldset class="space-y-3" disabled={!!teamConflict || followupMutation.isPending || updateStatusMutation.isPending || satisfactionMutation.isPending || handoffMutation.isPending}>
            <div><label class="label" for="detail-next-action">Next action</label>
              <input id="detail-next-action" class="input" bind:value={nextAction} placeholder="e.g. Call the customer to confirm the replacement arrived" /></div>
            <div><label class="label" for="detail-next-date">Follow-up date</label>
              <input id="detail-next-date" type="date" class="input" bind:value={nextActionDate} /></div>
            <div><label class="label" for="detail-assignee">Assigned to</label>
              <select id="detail-assignee" class="input" bind:value={assigneeId}>
                <option value={null}>Unassigned</option>
                {#if assigneeId && !teamUsers.some((u) => u.id === assigneeId)}<option value={assigneeId} disabled>{userName(assigneeId)} (no longer on team)</option>{/if}
                {#each teamUsers as u (u.id)}<option value={u.id}>{u.name}</option>{/each}
              </select></div>
            {#if nextActionDate}<p class="text-xs text-toyama-teal">Due {dueLabel(nextActionDate)} {#if dueBadge(nextActionDate)}<span class="rounded bg-[#FBF0DC] px-2 py-0.5 font-bold text-[#8a5c00]">{dueBadge(nextActionDate)}</span>{/if}</p>{/if}
            <div class="flex flex-wrap gap-2">
              <button type="button" class="btn-primary" disabled={!followupDirty} onclick={() => saveFollowup()}>Save follow-up</button>
              {#if followupDirty}<button type="button" class="btn-secondary" onclick={discardFollowup}>Discard changes</button>{/if}
              <button type="button" class="btn-secondary" disabled={!issue.next_action && !issue.next_action_date && !nextAction && !nextActionDate} onclick={() => saveFollowup(true)}>Clear follow-up</button>
            </div>
          </fieldset>
          <p class="text-xs text-dark-gray">Clear removes only the next action and date, not the assignee or issue status.</p>
          <p class="text-xs text-toyama-teal" role="status">{followupMutation.isPending ? 'Saving follow-up…' : followupDirty ? 'Unsaved follow-up changes' : followupMessage}</p>
          {#if followupError}<p class="text-xs text-red-600" role="alert">{followupError}</p>{/if}
        {:else if issue.status === 'resolved'}
          <p class="text-sm text-dark-gray">No outstanding action — this record is closed.</p>
          {#if issue.next_action || issue.next_action_date}<p class="text-xs text-dark-gray">Previous reminder: {issue.next_action}{issue.next_action_date ? ` · ${dueLabel(issue.next_action_date)}` : ''}</p>{/if}
        {:else if issue.next_action || issue.next_action_date}
          <p class="text-sm text-toyama-teal">{issue.next_action || 'Follow-up scheduled'}</p>
          {#if issue.next_action_date}<p class="text-xs text-dark-gray">Due {dueLabel(issue.next_action_date)} <span class="font-bold">{dueBadge(issue.next_action_date)}</span></p>{/if}
        {:else}<p class="text-sm text-dark-gray">No follow-up scheduled yet.</p>{/if}
      </section>

      {#if !isIssuesOnly}
        <section class="mb-4 space-y-3 rounded-xl border border-[#EFEEED] bg-[#FBFBFA] p-4" aria-label="Customer callback">
          <h4 class="font-heading text-sm font-bold text-navy">Customer callback</h4>
          <p class="text-xs text-dark-gray">Last actual customer contact: {issue.last_customer_contact_at ? `${timeAgo(issue.last_customer_contact_at)} · ${formatIST(issue.last_customer_contact_at)}` : 'No customer call recorded'}</p>
          <p class="text-xs text-dark-gray">Next customer call: {issue.customer_followup_at ? `${dueLabel(issue.customer_followup_at)} (UTC)` : 'No date set'}</p>
          <p class="text-xs text-dark-gray">Customer follow-up continues after handover or resolution. Ordinary notes and the satisfaction picker do not record an actual phone call.</p>
          {#if customerCallOpen}
            {#key `${issue.id}:${issue.vertical_id}`}<CustomerCallForm issue={issue} onSaved={() => (customerCallOpen = false)} onCancel={() => (customerCallOpen = false)} />{/key}
          {:else}
            <button type="button" class="btn-secondary" disabled={!!teamConflict || handoffBusy || issueQuery.isFetching} onclick={() => (customerCallOpen = true)}>Log customer call</button>
          {/if}
        </section>
      {/if}

      <section class="mb-4 space-y-2 rounded-xl border border-[#EFEEED] bg-[#FBFBFA] p-4" aria-label="Customer satisfaction">
        <label class="label" for="customer-satisfaction">Customer satisfaction</label>
        <select id="customer-satisfaction" class="input" value={issue.customer_satisfaction ?? ''}
          disabled={!!teamConflict || isIssuesOnly || satisfactionMutation.isPending || followupMutation.isPending || updateStatusMutation.isPending || handoffMutation.isPending}
          onchange={(e) => saveSatisfaction(e.currentTarget.value)}>
          {#each satisfactionOptions as option}<option value={option.value} disabled={option.value === 'satisfied' && issue.status !== 'resolved'}>{option.label}</option>{/each}
        </select>
        <p class="text-xs text-dark-gray">Resolve the work first, then confirm customer satisfaction. Each new resolution resets satisfaction to pending. Written or email confirmation can be recorded here; this picker does not record an actual phone call. Use Customer callback → Log customer call for that. Satisfaction does not replace CAPA.</p>
      </section>

      <div class="mb-4 rounded-xl border border-[#EFEEED] bg-[#FBFBFA] px-4">
        <div class="border-b border-[#F0F0F0] py-2.5 text-[13px] text-heavy-carbon">
          {issue.category === 'customer_feedback' ? 'Customer feedback' : issue.category === 'customer_log' ? 'Customer conversation' : 'Issue / complaint'}
        </div>
        {#if issue.company_name}
          <div class="flex items-baseline gap-3.5 border-b border-[#F0F0F0] py-2.5">
            <span class="w-[92px] shrink-0 text-xs font-medium text-dark-gray">Company</span>
            <span
              class="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11.5px] font-semibold"
              style="background:#E3F0F1; color:#226A73"
              >{issue.company_name}</span
            >
          </div>
        {/if}
        {#if issue.unit}
          <div class="flex items-baseline gap-3.5 border-b border-[#F0F0F0] py-2.5">
            <span class="w-[92px] shrink-0 text-xs font-medium text-dark-gray">Building / block / unit</span>
            <span
              class="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11.5px] font-semibold"
              style="background:#E3F0F1; color:#226A73"
              ><MapPin class="h-[11px] w-[11px]" />{issue.unit}</span
            >
          </div>
        {/if}
        {#if issue.room}
          <div class="flex items-baseline gap-3.5 border-b border-[#F0F0F0] py-2.5">
            <span class="w-[92px] shrink-0 text-xs font-medium text-dark-gray">Room / flat / area</span>
            <span class="flex-1 text-[13px] font-medium text-heavy-carbon">{issue.room}</span>
          </div>
        {/if}
        {#if issue.site_type || issue.location}
          <div class="border-b border-[#F0F0F0] py-2.5 text-[13px] text-heavy-carbon">
            <span class="text-xs text-dark-gray">Site / address</span>
            <div class="whitespace-pre-wrap">{issue.site_type ? `${issue.site_type} · ` : ''}{issue.location ?? ''}</div>
          </div>
        {/if}
        {#if issue.assignee_user_id}
          <div class="flex items-baseline gap-3.5 border-b border-[#F0F0F0] py-2.5">
            <span class="w-[92px] shrink-0 text-xs font-medium text-dark-gray">Assignee</span>
            <span class="flex-1 text-[13px] font-medium text-heavy-carbon">{userName(issue.assignee_user_id)}</span>
          </div>
        {/if}
        {#if issue.reporter_name || issue.reporter_phone || issue.reporter_email}
          <div class="flex items-baseline gap-3.5 border-b border-[#F0F0F0] py-2.5">
            <span class="w-[92px] shrink-0 text-xs font-medium text-dark-gray">Reporter</span>
            <span class="flex-1 text-[13px] font-medium text-heavy-carbon"
              >{issue.reporter_name}{issue.reporter_phone ? ` · ${issue.reporter_phone}` : ''}{issue.reporter_email ? ` · ${issue.reporter_email}` : ''}</span
            >
          </div>
        {/if}
        <div class="flex items-baseline gap-3.5 py-2.5">
          <span class="w-[92px] shrink-0 text-xs font-medium text-dark-gray">Filed</span>
          <span class="flex-1 text-[13px] font-medium text-heavy-carbon">{timeAgo(issue.created_at)}</span>
        </div>
      </div>

      {#if issue.body}
        <p class="mb-4 whitespace-pre-wrap text-[13.5px] font-light leading-[1.65] text-heavy-carbon">{issue.body}</p>
      {/if}

      {#if issue.status === 'resolved' && (issue.capa_root_cause || issue.capa_corrective_action || issue.capa_preventive_action)}
        <div class="mb-4 rounded-xl border border-[#EFEEED] bg-[#FBFBFA] px-4">
          <div class="border-b border-[#F0F0F0] py-2.5">
            <div class="mb-1 text-xs font-medium text-dark-gray">Root cause</div>
            <div class="whitespace-pre-wrap text-[13px] font-medium text-heavy-carbon">{issue.capa_root_cause}</div>
          </div>
          <div class="border-b border-[#F0F0F0] py-2.5">
            <div class="mb-1 text-xs font-medium text-dark-gray">Corrective action</div>
            <div class="whitespace-pre-wrap text-[13px] font-medium text-heavy-carbon">{issue.capa_corrective_action}</div>
          </div>
          <div class="py-2.5">
            <div class="mb-1 text-xs font-medium text-dark-gray">Preventive action</div>
            <div class="whitespace-pre-wrap text-[13px] font-medium text-heavy-carbon">{issue.capa_preventive_action}</div>
          </div>
        </div>
      {/if}

      {#if (attachmentsQuery.data?.results.length ?? 0) > 0}
        <div class="mb-2 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">Photos</div>
        <div class="mb-1.5 flex flex-wrap gap-2.5">
          {#each attachmentsQuery.data?.results ?? [] as ph (ph.id)}
            <button
              type="button"
              class="h-[76px] w-[104px] cursor-zoom-in overflow-hidden rounded-[10px] border border-toyama-cloudy-gray bg-[#F1F0EF] p-0 hover:border-toyama-teal"
              onclick={() => blobUrls[ph.id] && (lightboxUrl = blobUrls[ph.id])}
            >
              {#if blobUrls[ph.id]}
                <img src={blobUrls[ph.id]} alt={ph.original_filename} class="block h-full w-full object-cover" />
              {/if}
            </button>
          {/each}
        </div>
      {/if}

      <div class="my-4 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">Customer interaction log &amp; activity</div>
      <p class="mb-3 text-xs text-dark-gray">Record calls, visits, messages, feedback and outcomes here. Internal notes only — not sent to customers.</p>
      <div class="flex flex-col gap-2.5">
        {#each commentsQuery.data?.results ?? [] as t (t.id)}
          {#if t.kind === 'status_change'}
            <div class="flex items-center gap-2 px-1 py-0.5">
              <span
                class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#F1F0EF] text-dark-gray"
              >
                <ChevronDown class="h-3 w-3" />
              </span>
              <span class="text-xs text-dark-gray"
                >Status changed to {STATUS_META[t.body.split(' → ')[1] as IssueStatus]?.label ?? t.body}
                <span class="text-[#C9CDCE]">&middot; {timeAgo(t.created_at)}</span></span
              >
            </div>
          {:else}
            <div class="flex gap-2.5">
              <span
                class="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full font-heading text-[11px] font-bold"
                style="background:#E3F0F1; color:#226A73"
              >
                {userName(t.user_id).slice(0, 2).toUpperCase()}
              </span>
              <div class="flex-1 rounded-tl-[4px] rounded-tr-xl rounded-br-xl rounded-bl-xl border border-[#EFEEED] bg-toyama-light-carbon px-3.5 py-2.5">
                <div class="flex items-baseline gap-2">
                  <span class="text-[12.5px] font-bold text-navy">{userName(t.user_id)}</span>
                  <span class="text-[11px] text-dark-gray">{timeAgo(t.created_at)}</span>
                </div>
                <div class="mt-0.5 whitespace-pre-wrap text-[13px] leading-[1.55] text-heavy-carbon">{t.body}</div>
              </div>
            </div>
          {/if}
        {:else}
          <div class="px-0.5 py-1 text-[12.5px] text-dark-gray">
            No interactions yet. Log the next customer conversation here; external reporters cannot see this thread.
          </div>
        {/each}
      </div>
    </div>

    <div class="shrink-0 border-t border-[#EAE9E8] bg-white px-4 py-3">
      <div class="mb-2 flex flex-wrap gap-2 text-sm">
        {#if issue.reporter_phone}<a class="btn-secondary" href={`tel:${issue.reporter_phone}`}>Call customer</a>{/if}
        {#if issue.location}<a class="btn-secondary" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(issue.location)}`} target="_blank" rel="noopener noreferrer">Directions</a>{/if}
        {#if isIssuesOnly && issue.status !== 'resolved'}
          <button type="button" class="btn-secondary" onclick={() => prefillOutcomeNote('Fixed')}>Add fixed-work note</button>
          <button type="button" class="btn-secondary" onclick={() => prefillOutcomeNote('Need parts')}>Add parts-needed note</button>
          <p class="w-full text-xs text-dark-gray">These shortcuts only draft a note; they do not resolve the case or schedule parts. Send completion to the configured coordinator, who records the technician and required resolution details.</p>
        {/if}
      </div>
      <p class="mb-2 text-xs text-dark-gray" role="status">{draftMessage || 'Notes are saved as drafts on this device until posted.'}</p>
      <div class="flex items-end gap-2.5">
      <textarea
        bind:this={commentInput}
        bind:value={commentText}
        aria-label="Interaction note, saved as a draft on this device"
        placeholder="Log a call, visit, message or internal update…"
        rows="1"
        class="input min-h-[44px] max-h-[120px] flex-1 resize-none !bg-toyama-light-carbon"
      ></textarea>
      <input type="file" accept="image/*" multiple class="hidden" bind:this={fileInput} onchange={onFileChosen} />
      <button
        type="button"
        title="Attach photos (camera or library)"
        aria-label="Attach photos (camera or library)"
        class="btn-secondary !h-11 !w-11 shrink-0 !p-0"
        onclick={() => fileInput?.click()}
        disabled={photoUploads.some(p => p.status === 'uploading' || p.status === 'waiting')}
      >
        <Paperclip class="h-[18px] w-[18px]" />
      </button>
      <button
        type="button"
        class="flex h-11 shrink-0 items-center gap-1.5 rounded-[11px] bg-toyama-lime px-4 font-heading text-[13px] font-bold text-navy hover:bg-[var(--tym-lime-600)] disabled:opacity-50"
        onclick={sendComment}
        disabled={sendCommentMutation.isPending || !commentText.trim()}
      >
        <Send class="h-4 w-4" />
        Log interaction
      </button>
      </div>
      {#each photoUploads.filter(p => p.issueId === issueId) as photo (photo.key)}
        <div class="mt-2 flex items-center gap-2 text-xs" role="status"><span class="min-w-0 flex-1 break-all">{photo.file.name}: {photo.status}{photo.error ? ` — ${photo.error}` : ''}</span>{#if photo.status === 'failed'}<button type="button" class="btn-secondary" onclick={() => uploadPhoto(photo)}>Retry</button>{/if}</div>
      {/each}
    </div>
  {:else if issueQuery.isError}
    <div class="flex flex-1 items-center justify-center p-6 text-sm text-red-600">
      {getErrorMessage(issueQuery.error)}
    </div>
  {:else}
    <div class="flex flex-1 items-center justify-center text-sm text-dark-gray">Loading issue&hellip;</div>
  {/if}
</div>

{#if lightboxUrl}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="issue-lightbox" role="presentation" onclick={() => (lightboxUrl = null)}>
    <img
      src={lightboxUrl}
      alt="Attachment full size"
      class="max-h-[88%] max-w-[88%] rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,.5)]"
      style="animation: tym-scale-in .2s var(--tym-ease-out)"
    />
  </div>
{/if}
